import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Panel from '../../common/layout/Panel';
import { getList } from '../../common/v4/BackendProvider';

const DashboardSmpp = () => {
    const [smppAccounts, setSmppAccounts] = useState([]);
    const [carrierSmppAccounts, setCarrierSmppAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [smsServers, setSmsServers] = useState([]);
    const refreshIntervalRef = useRef(null);

    // Function to fetch all SMPP accounts
    const fetchSmppAccounts = async () => {
        setLoading(true);
        let clientAccounts = [];
        let carrierAccounts = [];
        let hasErrors = false;
        
        // Fetch client SMPP accounts
        try {
            const clientResponse = await getList('/admin/smpp/accounts');
            if (clientResponse.data?.result === 'OK') {
                clientAccounts = clientResponse.data.data || [];
            }
        } catch (error) {
            console.error('Error fetching client SMPP accounts:', error);
            toast.error('Failed to fetch client SMPP accounts');
            hasErrors = true;
        }
        
        // Fetch carrier SMPP accounts
        try {
            const carrierResponse = await getList('/admin/smpp/carrier-accounts');
            if (carrierResponse.data?.result === 'OK') {
                carrierAccounts = carrierResponse.data.data || [];
            }
        } catch (error) {
            console.error('Error fetching carrier SMPP accounts:', error);
            toast.error('Failed to fetch carrier SMPP accounts');
            hasErrors = true;
        }
        
        // Set the data even if one of the fetches failed
        setSmppAccounts(clientAccounts);
        setCarrierSmppAccounts(carrierAccounts);
        
        // Extract unique servers from both responses
        const servers = new Set();
        [...clientAccounts, ...carrierAccounts].forEach(account => {
            if (account.server_connections) {
                account.server_connections.forEach(server => {
                    servers.add(server.server_name);
                });
            }
        });
        setSmsServers(Array.from(servers));
        
        // Only show error toast if both fetches failed
        if (hasErrors && clientAccounts.length === 0 && carrierAccounts.length === 0) {
            toast.error('Failed to fetch SMPP accounts');
        }
        
        setLoading(false);
    };

    // Function to fetch connection status for all usernames
    const fetchConnectionStatus = useCallback(async () => {
        // Connection data is now included in the main API response
        // No need for separate API calls
    }, [smppAccounts]);

    // Initial data fetch
    useEffect(() => {
        fetchSmppAccounts();
    }, []);

    // Set up auto-refresh every 60 seconds
    useEffect(() => {
        if (smppAccounts.length > 0) {
            // Set up auto-refresh
            refreshIntervalRef.current = setInterval(() => {
                fetchSmppAccounts(); // Refresh the main data
            }, 60000); // 60 seconds
            
            return () => {
                if (refreshIntervalRef.current) {
                    clearInterval(refreshIntervalRef.current);
                    refreshIntervalRef.current = null;
                }
            };
        }
    }, [smppAccounts]);

    const getStatusBadge = (enabled) => {
        return enabled ? (
            <span className="badge" style={{ backgroundColor: '#198754', color: '#198754', marginRight: '16px' }}>
                ●
            </span>
        ) : (
            <span className="badge" style={{ backgroundColor: '#dc3545', color: '#dc3545', marginRight: '16px' }}>
                ●
            </span>
        );
    };

    const getConnectionDot = (connectionCount) => {
        if (connectionCount > 0) {
            return (
                <span className="badge" style={{ backgroundColor: '#198754', color: '#198754', marginRight: '16px' }}>
                    ●
                </span>
            );
        } else {
            return (
                <span className="badge" style={{ backgroundColor: '#dc3545', color: '#dc3545', marginRight: '16px' }}>
                    ●
                </span>
            );
        }
    };

    const getAccountTypeDot = (type) => {
        if (type === 'client') {
            return (
                <span className="badge" style={{ backgroundColor: '#0d6efd', color: '#0d6efd', marginRight: '16px' }}>
                    ●
                </span>
            );
        } else {
            return (
                <span className="badge" style={{ backgroundColor: '#fd7e14', color: '#fd7e14', marginRight: '16px' }}>
                    ●
                </span>
            );
        }
    };

    return (
        <>
            <ToastContainer position="top-right" autoClose={5000} />
            <Panel title="SMPP Status Overview">
                <>
                <div className="table-responsive">
                    <table className="table table-bordered table-striped">
                        <thead>
                            <tr>
                                <th>Account Type</th>
                                <th>ID & Name</th>
                                <th>Status</th>
                                <th>Credit (Used/Limit)</th>
                                <th>SMPP Username</th>
                                <th>SMPP Status</th>
                                {smsServers.map((server, index) => (
                                    <th key={index} className="text-center">
                                        <div>{server}</div>
                                        <small className="text-muted">Bind Count</small>
                                    </th>
                                ))}
                                <th className="text-center">
                                    <div>Total</div>
                                    <small className="text-muted">Connections</small>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7 + smsServers.length} className="text-center">
                                        <div className="spinner-border spinner-border-sm me-2" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        Loading SMPP accounts...
                                    </td>
                                </tr>
                            ) : (smppAccounts.length === 0 && carrierSmppAccounts.length === 0) ? (
                                <tr>
                                    <td colSpan={7 + smsServers.length} className="text-center text-muted">
                                        No SMPP accounts found
                                    </td>
                                </tr>
                            ) : (
                                (() => {
                                    // Process client accounts
                                    const clientGroupedAccounts = {};
                                    smppAccounts.forEach(account => {
                                        const clientId = account.client?.ATRCLIENTID;
                                        if (!clientGroupedAccounts[clientId]) {
                                            clientGroupedAccounts[clientId] = [];
                                        }
                                        clientGroupedAccounts[clientId].push({...account, type: 'client'});
                                    });

                                    // Process carrier accounts
                                    const carrierGroupedAccounts = {};
                                    carrierSmppAccounts.forEach(account => {
                                        const carrierId = account.carrier?.ATRCARRIERID;
                                        if (!carrierGroupedAccounts[carrierId]) {
                                            carrierGroupedAccounts[carrierId] = [];
                                        }
                                        carrierGroupedAccounts[carrierId].push({...account, type: 'carrier'});
                                    });

                                    // Combine and render all accounts
                                    const allAccounts = [];

                                    // Add client accounts
                                    Object.entries(clientGroupedAccounts).forEach(([clientId, accounts]) => {
                                        const firstAccount = accounts[0];
                                        const client = firstAccount.client;
                                        
                                        accounts.forEach((account, accountIndex) => {
                                            allAccounts.push({
                                                ...account,
                                                groupId: clientId,
                                                groupName: client?.ATRACCTNAME || 'Unknown Client',
                                                groupNumber: client?.ATRCLIENTIDNUMBER || 'N/A',
                                                groupStatus: client?.ATRSTATUS,
                                                groupBalance: client?.ATRBALANCE,
                                                groupCreditLimit: client?.ATRCREDITLIMIT,
                                                isFirstInGroup: accountIndex === 0,
                                                groupLength: accounts.length
                                            });
                                        });
                                    });

                                    // Add carrier accounts
                                    Object.entries(carrierGroupedAccounts).forEach(([carrierId, accounts]) => {
                                        const firstAccount = accounts[0];
                                        const carrier = firstAccount.carrier;
                                        
                                        accounts.forEach((account, accountIndex) => {
                                            allAccounts.push({
                                                ...account,
                                                groupId: carrierId,
                                                groupName: carrier?.ATRNAME || 'Unknown Carrier',
                                                groupNumber: carrier?.ATRCARRIERID || 'N/A',
                                                groupStatus: carrier?.ATRSTATUS,
                                                groupBalance: carrier?.ATRBALANCE,
                                                groupCreditLimit: carrier?.ATRCREDLIM,
                                                isFirstInGroup: accountIndex === 0,
                                                groupLength: accounts.length
                                            });
                                        });
                                    });

                                    return allAccounts.map((account, index) => (
                                        <tr key={`${account.type}-${account.groupId}-${index}`}>
                                            {account.isFirstInGroup ? (
                                                <>
                                                    <td rowSpan={account.groupLength}>
                                                        {getAccountTypeDot(account.type)}
                                                        {account.type === 'client' ? 'Client' : 'Carrier'}
                                                    </td>
                                                    <td rowSpan={account.groupLength}>
                                                        <strong>{account.groupNumber}</strong>
                                                        <br />
                                                        <small className="text-muted">{account.groupName}</small>
                                                    </td>
                                                    <td rowSpan={account.groupLength}>
                                                        {getStatusBadge(account.groupStatus)}
                                                        {account.groupStatus ? 'Enabled' : 'Disabled'}
                                                    </td>
                                                    <td rowSpan={account.groupLength}>
                                                        {(() => {
                                                            const balance = account.groupBalance ? parseFloat(account.groupBalance) : 0;
                                                            const creditLimit = account.groupCreditLimit ? parseFloat(account.groupCreditLimit) : 0;
                                                            const isOverLimit = balance < -creditLimit;
                                                            
                                                            return (
                                                                <>
                                                                    <span className={isOverLimit ? "text-danger" : "text-success"}>
                                                                        {balance.toFixed(2)}
                                                                    </span>
                                                                    <span style={{ margin: '0 0 0 20px' }}> / </span>
                                                                    <span className={isOverLimit ? "text-danger" : "text-info"}>
                                                                        {creditLimit.toFixed(2)}
                                                                    </span>
                                                                </>
                                                            );
                                                        })()}
                                                    </td>
                                                </>
                                            ) : null}
                                            <td>
                                                <code>{account.ATRUSERNAME || account.username}</code>
                                            </td>
                                            <td>
                                                {getStatusBadge(account.ATRSTATUS || account.status)}
                                                {account.ATRSTATUS || account.status ? 'Enabled' : 'Disabled'}
                                            </td>
                                            {smsServers.map((serverName, serverIndex) => {
                                                const serverConnection = account.server_connections.find(
                                                    sc => sc.server_name === serverName
                                                );
                                                const connectionCount = serverConnection ? serverConnection.connections.length : 0;
                                                
                                                return (
                                                    <td key={serverIndex} className="text-center">
                                                        {getConnectionDot(connectionCount)}
                                                        {connectionCount}
                                                    </td>
                                                );
                                            })}
                                            <td className="text-center">
                                                {getConnectionDot(account.connection_count || 0)}
                                                <strong>{account.connection_count || 0}</strong>
                                            </td>
                                        </tr>
                                    ));
                                })()
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="mt-3">
                    <small className="text-muted">
                        <i className="fas fa-sync-alt me-1"></i>
                        Auto-refreshing every 60 seconds
                    </small>
                </div>
                </>
            </Panel>
        </>
    );
};

export default DashboardSmpp;